import { useEffect } from "react"
import { Form, Input, Flex } from "antd"
import { styled } from "styled-components"
import { BetaTag } from "src/global/ui/Tag/BetaTag.tsx"
import { EXPECTATION_PROMPT_TEXT } from "src/pages/DataAssets/views/Expectations/SimpleExpectationDrawer/ExpectationPrompt/words"
import { FormInstance } from "antd/es/form"
import { AlertBanner } from "src/global/ui/Alert/AlertBanner.tsx"
import { LoadingMessage } from "src/global/ui/LoadingState/LoadingMessage.tsx"
import { theme } from "src/global/ui/themes/theme.ts"

const StyledFormItemWrapper = styled.div`
  position: relative;
`

const PositionedBetaTag = styled(BetaTag)`
  position: absolute;
  top: 0; /* Adjust to align with label text */
  right: -8px;
  line-height: 1.5715; /* Match Ant Design label line height */
`

type Props = {
  form: FormInstance
  promptText: string
  setPromptText: (text: string) => void
  expectAiError: string | null
  isGeneratingExpectation: boolean
}

export function ExpectationPromptBox({
  form,
  promptText,
  setPromptText,
  expectAiError,
  isGeneratingExpectation,
}: Props) {
  // Set the form field value to match promptText when it changes
  useEffect(() => {
    form.setFieldsValue({ promptText })
  }, [form, promptText])

  return (
    <Form form={form} layout="vertical">
      <StyledFormItemWrapper>
        <Form.Item
          name="promptText"
          label={EXPECTATION_PROMPT_TEXT.LABEL}
          tooltip={EXPECTATION_PROMPT_TEXT.TOOLTIP}
          style={{ marginBottom: 8 }}
          rules={[
            { required: true, message: "Please enter a prompt" },
            {
              validator: (_, value) => {
                if (!value || value.length <= 4000) {
                  return Promise.resolve()
                }
                return Promise.reject(
                  new Error(`Your prompt is ${value.length} characters long, which exceeds the 4000 character limit`),
                )
              },
            },
          ]}
        >
          <Input.TextArea
            onChange={(e) => setPromptText(e.target.value)}
            placeholder={EXPECTATION_PROMPT_TEXT.PLACEHOLDER}
            rows={4}
            disabled={isGeneratingExpectation}
            data-testid="generate-expectation-prompt-input"
          />
        </Form.Item>
        <PositionedBetaTag />
      </StyledFormItemWrapper>
      {expectAiError && <AlertBanner message={expectAiError} type="error" style={{ marginTop: theme.spacing.s }} />}
      {isGeneratingExpectation && (
        <Flex justify="center" align="center" style={{ paddingTop: theme.spacing.xxs, paddingBottom: theme.spacing.s }}>
          <LoadingMessage loading={true} message={EXPECTATION_PROMPT_TEXT.LOADING} />
        </Flex>
      )}
    </Form>
  )
}
